import consumer from "./consumer"
import { ChatState } from "../chat/state"

// Notification sound — uses Web Audio API with HTMLAudio fallback for Safari.
const SOUND_URL = '/sounds/notification.mp3'
// Distinct sound for permit chats so they're audibly different from the master chat.
const PERMIT_SOUND_URL = '/sounds/permit_notification.mp3'
const AudioCtx = window.AudioContext || window.webkitAudioContext
let audioContext = null
let notificationBuffer = null
let permitBuffer = null
let audioUnlocked = false
let fallbackAudio = null

function getVolume() {
  return ChatState.notificationSettings.notification_volume ?? 0.5
}

// Permit-chat alerts play quieter than the master chat: they fire per project,
// so at equal volume they dominate. Scales the user's chosen volume instead of
// replacing it, so muting still mutes.
const PERMIT_VOLUME_SCALE = 0.35

function getPermitVolume() {
  return getVolume() * PERMIT_VOLUME_SCALE
}

function getSoundUrl() {
  return document.querySelector('.fab-side-panel')?.getAttribute('data-chat-notification-sound') || SOUND_URL
}

function ensureAudioContext() {
  if (!audioContext && AudioCtx) {
    audioContext = new AudioCtx()
  }
  return audioContext
}

function loadNotificationBuffer() {
  const ctx = ensureAudioContext()
  if (!ctx || notificationBuffer) return

  fetch(getSoundUrl())
    .then(res => res.arrayBuffer())
    .then(buf => ctx.decodeAudioData(buf))
    .then(decoded => { notificationBuffer = decoded })
    .catch(() => {})
}

function loadPermitBuffer() {
  const ctx = ensureAudioContext()
  if (!ctx || permitBuffer) return

  fetch(PERMIT_SOUND_URL)
    .then(res => res.arrayBuffer())
    .then(buf => ctx.decodeAudioData(buf))
    .then(decoded => { permitBuffer = decoded })
    .catch(() => {})
}

function unlockAudio() {
  if (audioUnlocked) return
  audioUnlocked = true

  const ctx = ensureAudioContext()
  if (ctx && ctx.state === 'suspended') {
    ctx.resume().catch(() => {})
  }
  loadNotificationBuffer()
  loadPermitBuffer()

  // Pre-create fallback Audio element during user gesture (Safari requires this)
  try {
    fallbackAudio = new Audio(getSoundUrl())
    fallbackAudio.volume = getVolume()
    fallbackAudio.load()
  } catch (e) {}
}

async function playNotificationAudio(volume = getVolume()) {
  const ctx = ensureAudioContext()

  // Resume suspended AudioContext (happens after period of inactivity)
  if (ctx && ctx.state === 'suspended') {
    try { await ctx.resume() } catch (e) {}
  }

  // Ensure notification buffer is loaded (may have failed on initial load)
  if (ctx && !notificationBuffer && ctx.state === 'running') {
    loadNotificationBuffer()
  }

  // Try Web Audio API first
  if (ctx && notificationBuffer && ctx.state === 'running') {
    try {
      const source = ctx.createBufferSource()
      const gain = ctx.createGain()
      gain.gain.value = volume
      source.buffer = notificationBuffer
      source.connect(gain)
      gain.connect(ctx.destination)
      source.start(0)
      return
    } catch (e) {}
  }

  // Fallback: HTMLAudioElement (works in Safari after user gesture)
  try {
    if (fallbackAudio) {
      fallbackAudio.volume = volume
      fallbackAudio.currentTime = 0
      fallbackAudio.play().catch(() => {})
    } else {
      const audio = new Audio(getSoundUrl())
      audio.volume = volume
      audio.play().catch(() => {})
    }
  } catch (e) {}
}

// Distinct alert for permit chats: plays the dedicated permit sound file so a
// permit-chat message is audibly different from a master-chat message. Mirrors
// playNotificationAudio (Web Audio first, HTMLAudio fallback for Safari), and
// falls back to the standard notification sound if the permit sound can't play.
async function playPermitChatAudio() {
  const ctx = ensureAudioContext()

  if (ctx && ctx.state === 'suspended') {
    try { await ctx.resume() } catch (e) {}
  }
  if (ctx && !permitBuffer && ctx.state === 'running') {
    loadPermitBuffer()
  }

  if (ctx && permitBuffer && ctx.state === 'running') {
    try {
      const source = ctx.createBufferSource()
      const gain = ctx.createGain()
      gain.gain.value = getPermitVolume()
      source.buffer = permitBuffer
      source.connect(gain)
      gain.connect(ctx.destination)
      source.start(0)
      return
    } catch (e) {}
  }

  // Fallback: HTMLAudioElement, then the standard notification sound.
  try {
    const audio = new Audio(PERMIT_SOUND_URL)
    audio.volume = getPermitVolume()
    await audio.play()
  } catch (e) {
    playNotificationAudio(getPermitVolume())
  }
}

// Unlock audio on first user interaction (required by Safari autoplay policy)
document.addEventListener('click', unlockAudio, { capture: true, once: false })
document.addEventListener('touchstart', unlockAudio, { capture: true, once: false })
document.addEventListener('keydown', unlockAudio, { capture: true, once: false })

// Also try to load buffers eagerly (works in Chrome/Firefox without gesture)
loadNotificationBuffer()
loadPermitBuffer()

const NotificationChannel = {
  subscription: null,

  subscribe(callbacks) {
    // Swap callbacks instead of resubscribing (see ChatChannel#subscribe)
    if (this.subscription && consumer.subscriptions.subscriptions.includes(this.subscription)) {
      this.subscription.callbacks = callbacks
      return this.subscription
    }

    this.subscription = consumer.subscriptions.create(
      { channel: "NotificationChannel" },
      {
        callbacks: callbacks,

        connected() {
          console.log('Notification channel connected')
          if (this.callbacks.onConnected) this.callbacks.onConnected()
        },

        disconnected() {
          console.log('Notification channel disconnected')
          if (this.callbacks.onDisconnected) this.callbacks.onDisconnected()
        },

        received(data) {
          if (data.type === 'read_update') {
            if (data.unread_count !== undefined) {
              this.updateNotificationBadge(data.unread_count)
            }
            this.updatePermitChatNavBadge(data.conversation_id, data.conversation_unread_count, data.project_id)
            if (this.callbacks.onReadUpdate) {
              this.callbacks.onReadUpdate(data)
            }
            return
          }

          const isNewMessage = data.type === 'new_message'
          const isNewNotification = data.type === 'new_notification'
          if (!isNewMessage && !isNewNotification) return

          const notification = data.notification || {
            title: 'New message',
            body: 'You have a new message',
            chat_conversation_id: data.conversation_id
          }

          // onNewNotification may return true to suppress browser notification
          // (e.g., when the user is already viewing the conversation)
          // Pass full data so callback can access message payload for display
          const suppressBrowserNotif = this.callbacks.onNewNotification
            ? this.callbacks.onNewNotification(notification, data)
            : false

          if (data.unread_count !== undefined) {
            this.updateNotificationBadge(data.unread_count)
          }
          this.updatePermitChatNavBadge(data.conversation_id, data.conversation_unread_count, data.project_id)

          // Sound always respects user settings — never suppressed by viewing state.
          // Permit chats get a distinct tone, and it is mention-driven: only a
          // recipient named with @ in the message hears it; everyone else gets
          // the badge silently. Master chat sounds on every message as before.
          const playSound = typeof this.callbacks.playSound === 'function' ? this.callbacks.playSound() : this.callbacks.playSound
          if (playSound !== false) {
            if (data.is_permit_chat) {
              if (data.mentioned) playPermitChatAudio()
            } else {
              this.playNotificationSound()
            }
          }

          // Browser notification suppressed when user is already viewing the conversation
          if (!suppressBrowserNotif) {
            const showBrowserNotification = typeof this.callbacks.showBrowserNotification === 'function'
              ? this.callbacks.showBrowserNotification()
              : this.callbacks.showBrowserNotification
            if (showBrowserNotification !== false) {
              this.showBrowserNotification(notification)
            }
          }
        },

        updateNotificationBadge(count) {
          const badges = document.querySelectorAll('.fab-badge[data-chat-notification-badge], .notification-badge, [data-notification-badge], [data-chat-notification-badge]')
          badges.forEach(badge => {
            badge.textContent = count > 0 ? (count > 99 ? '99+' : String(count)) : ''
            badge.style.display = count > 0 ? 'flex' : 'none'
            badge.classList.toggle('has-notifications', count > 0)
          })
        },

        // Per-conversation badges (the project sidebar "Chats" item and the
        // projects-list row badge), keyed by conversation id so each reflects
        // only THIS chat — not the global total. The projects-list badge is also
        // keyed by project id: it is rendered before the conversation may exist
        // (first-ever message), so the conversation id can be unknown until now.
        updatePermitChatNavBadge(conversationId, count, projectId) {
          if (conversationId == null && projectId == null) return
          const selectors = []
          if (conversationId != null) {
            selectors.push(
              `[data-permit-chat-nav-badge][data-conversation-id="${conversationId}"]`,
              `[data-permit-chat-list-badge][data-conversation-id="${conversationId}"]`
            )
          }
          if (projectId != null) {
            selectors.push(`[data-permit-chat-list-badge][data-project-id="${projectId}"]`)
          }
          const badges = new Set(document.querySelectorAll(selectors.join(', ')))
          if (!badges.size) return
          // Adopt the conversation id on project-keyed badges so later
          // read_updates (keyed by conversation only) still find them.
          if (conversationId != null) {
            badges.forEach(badge => {
              if (!badge.getAttribute('data-conversation-id')) {
                badge.setAttribute('data-conversation-id', conversationId)
              }
            })
          }
          // If the user is currently on this permit's chat page, the messages are
          // being read — keep the badge cleared rather than flashing a count.
          const viewingThisChat = document.querySelector(`[data-permit-chat-root="true"][data-conversation-id="${conversationId}"]`)
          const effective = viewingThisChat ? 0 : (Number(count) || 0)
          const text = effective > 0 ? (effective > 99 ? '99+' : String(effective)) : ''
          badges.forEach(badge => {
            badge.textContent = text
            badge.style.display = effective > 0 ? 'inline-flex' : 'none'
          })
        },

        playNotificationSound() {
          try {
            playNotificationAudio()
          } catch (e) {
            console.log('Notification sound not available:', e)
          }
        },

        showBrowserNotification(notification) {
          if ('Notification' in window) {
            if (Notification.permission === 'granted') {
              new Notification(notification.title || 'New message', {
                body: notification.body || 'You have a new message',
                icon: notification.icon_url || '/favicon.ico',
                tag: `chat-${notification.chat_conversation_id}`,
                requireInteraction: false
              })
            } else if (Notification.permission === 'default') {
              Notification.requestPermission().then(permission => {
                if (permission === 'granted') {
                  this.showBrowserNotification(notification)
                }
              })
            }
          }
        }
      }
    )

    return this.subscription
  },

  unsubscribe() {
    if (this.subscription) {
      this.subscription.unsubscribe()
      this.subscription = null
    }
  }
}

export { playNotificationAudio }
export default NotificationChannel
